import { ContractLoader } from "../../domains/Contracts/components"
import { useDocumentContext } from "../../contexts/Document/DocumentContext"
import { useDropzone } from "react-dropzone"
import { useState } from "react"
import { useTranslation } from "react-i18next"

const MAX_SIZE_MB = 2

const FileUploader = ({
  onFileSelected,
  loading,
  error: externalError,
  onClearError,
}) => {
  const { t } = useTranslation()
  const [error, setError] = useState("")
  const { document } = useDocumentContext()

  // Combine local validation errors with external analysis errors
  const displayError = error || externalError

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    maxFiles: 1,
    maxSize: MAX_SIZE_MB * 1024 * 1024, // 2 MB

    onDrop: (acceptedFiles, fileRejections) => {
      // Clear errors when user starts new upload
      if (externalError && onClearError) {
        onClearError()
      }
      setError("")

      if (fileRejections.length > 0) {
        const rejection = fileRejections[0]

        if (rejection.errors.some((e) => e.code === "file-too-large")) {
          setError(t("dashboard.fileTooLarge", { size: MAX_SIZE_MB }))
        } else if (
          rejection.errors.some((e) => e.code === "file-invalid-type")
        ) {
          setError(t("dashboard.unsupportedFileType"))
        } else {
          setError(t("dashboard.failedToPickFile"))
        }

        return
      }

      onFileSelected(acceptedFiles[0])
    },
  })

  return (
    <div className="bg-dark-default rounded-[32px] px-6 py-8 w-full max-w-3xl mx-auto">
      <div className="bg-accent-default rounded-[24px] p-4 mb-5 md:mb-[52px]">
        <div className="text-[16px] font-bold text-dark-default mb-3">
          {t("dashboard.helpTitle")}
        </div>
        <div className="text-[16px] text-dark-default mb-3">
          {t("dashboard.helpDescription")}
        </div>
        <div className="flex flex-row items-baseline gap-1">
          <div className="text-[16px] font-bold text-dark-default">
            {t("dashboard.helpFooterTitle")}
          </div>
          <div className="text-[16px] text-dark-default">
            {t("dashboard.helpFooterText")}
          </div>
        </div>
      </div>

      <h2 className="text-[24px] font-bold text-center mb-3 text-light-default">
        {t("dashboard.uploadDocument")}
      </h2>

      <p className="text-center text-light-default mb-5 leading-5 text-[16px]">
        <span className="font-bold">
          {t("dashboard.uploadDescriptionBold")}
        </span>{" "}
        {t("dashboard.uploadDescription")}
      </p>

      <div
        {...getRootProps()}
        className={`rounded-[24px] border-2 border-dashed px-10 py-9 text-center cursor-pointer transition-all duration-300 min-h-[200px] bg-light-default mb-5 ${
          isDragActive ? "border-blue-500" : "border-dark-default"
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center w-full">
          <div className="text-[56px]">📁</div>

          {loading ? (
            <div className="w-full max-w-md mt-4">
              <ContractLoader loading={loading} document={document} />
            </div>
          ) : (
            <>
              <div className="text-[16px] font-bold text-dark-default mb-1 mt-3">
                {t("dashboard.selectFile")}
              </div>
            </>
          )}
        </div>
      </div>
      {displayError ? (
        <div className="mt-3 min-h-[20px] text-sm font-semibold text-center">
          <span className="text-danger-default">{displayError}</span>
        </div>
      ) : null}
      <div className="mt-2 text-[14px] text-light-default">
        <div className="flex items-center justify-between">
          <span>{t("dashboard.maxFileSize")}:</span>
          <span className="font-bold">
            {MAX_SIZE_MB} {t("units.megabytes")}
          </span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <span>{t("dashboard.supportedFormats")}:</span>
          <span className="font-bold">
            {t("dashboard.supportedFormatsValue")}
          </span>
        </div>
        <div>
          <span style={{ fontStyle: "italic" }}>
            {t("dashboard.supportedFormatsDescription")}
          </span>
        </div>
      </div>
    </div>
  )
}

export default FileUploader
